// ============================================================================
// FNE-BRIDGE - Store Articles / Produits / Nomenclature (Zustand)
// ============================================================================

import { create } from 'zustand';
import type { Product } from '../types/models';

// ============================================================================
// Types du state
// ============================================================================

interface ProductState {
  // --- Liste ---
  products: Product[];
  productsLoading: boolean;
  productsError: string | null;
  productsTotal: number;
  productsPage: number;
  productsLimit: number;

  // --- Produit courant ---
  currentProduct: Product | null;
  currentProductLoading: boolean;

  // --- Catégories ---
  categories: string[];

  // --- Filtres ---
  filters: {
    search: string;
    categorie: string;
    taux_tva: number | undefined;
    est_actif: boolean | undefined;
  };

  // --- Actions ---
  setProducts: (products: Product[], total: number) => void;
  setProductsLoading: (loading: boolean) => void;
  setProductsError: (error: string | null) => void;
  setProductsPage: (page: number) => void;
  setProductsLimit: (limit: number) => void;
  setCurrentProduct: (product: Product | null) => void;
  setCurrentProductLoading: (loading: boolean) => void;
  setCategories: (categories: string[]) => void;
  addProduct: (product: Product) => void;
  updateProduct: (id: string, updates: Partial<Product>) => void;
  removeProduct: (id: string) => void;
  setFilters: (filters: Partial<ProductState['filters']>) => void;
  reset: () => void;
}

// ============================================================================
// Store
// ============================================================================

const initialFilters = {
  search: '',
  categorie: '',
  taux_tva: undefined as number | undefined,
  est_actif: undefined as boolean | undefined,
};

export const useProductStore = create<ProductState>()((set) => ({
  products: [],
  productsLoading: false,
  productsError: null,
  productsTotal: 0,
  productsPage: 1,
  productsLimit: 20,
  currentProduct: null,
  currentProductLoading: false,
  categories: [],
  filters: { ...initialFilters },

  setProducts: (products, total) => set({ products, productsTotal: total, productsLoading: false }),
  setProductsLoading: (productsLoading) => set({ productsLoading }),
  setProductsError: (productsError) => set({ productsError, productsLoading: false }),
  setProductsPage: (productsPage) => set({ productsPage }),
  setProductsLimit: (productsLimit) => set({ productsLimit, productsPage: 1 }),

  setCurrentProduct: (currentProduct) => set({ currentProduct, currentProductLoading: false }),
  setCurrentProductLoading: (currentProductLoading) => set({ currentProductLoading }),

  setCategories: (categories) => set({ categories }),

  addProduct: (product) =>
    set((state) => ({
      products: [product, ...state.products],
      productsTotal: state.productsTotal + 1,
    })),

  updateProduct: (id, updates) =>
    set((state) => ({
      products: state.products.map((p) => (p.id === id ? { ...p, ...updates } : p)),
      currentProduct:
        state.currentProduct?.id === id
          ? { ...state.currentProduct, ...updates }
          : state.currentProduct,
    })),

  removeProduct: (id) =>
    set((state) => ({
      products: state.products.filter((p) => p.id !== id),
      productsTotal: state.productsTotal - 1,
      currentProduct: state.currentProduct?.id === id ? null : state.currentProduct,
    })),

  setFilters: (newFilters) =>
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      productsPage: 1,
    })),

  reset: () =>
    set({
      products: [],
      productsLoading: false,
      productsError: null,
      productsTotal: 0,
      productsPage: 1,
      currentProduct: null,
      currentProductLoading: false,
      categories: [],
      filters: { ...initialFilters },
    }),
}));
